import React, { useState } from 'react';
import { Switch, Route, RouteComponentProps, withRouter } from 'react-router-dom';
import { Flex, Box, color, Button, Typography, Tabs, Tab } from '@kistech/kis-ui';
import { actions as authActions, selectors as authSelectors } from 'store/auth';
import { actions as venueActions } from 'store/venues';
import { useActions } from 'hooks/useActions';
import { getMatchPath } from 'utils/urlUtils';
import packageJSON from '../../package.json';
import UsersView from 'views/UsersView';
import KiosksView from 'views/KiosksView';
import ActivityLogView from 'views/ActivityLogView';
import DeliveryLogView from 'views/DeliveryLogView';
import MediaCenterView from 'views/MediaCenterView';
import ConfigurationView from 'views/ConfigurationView';
import SettingsView from 'views/SettingsView';
import { useStylesDashboard } from './styled';
import { Grid } from '@mui/material';
import { useSelector } from 'react-redux';

// RETURNS THE PATHNAME WITHOUT THE KIOSKID
const pathInit = (path: string) => {
  const match = getMatchPath(path, '/kiosks/:kioskId?');

  // IF THERE IS A MATCH OF PATH AND THAT PATH HAS KIOSKID, SLICE IT OUT
  if (match && match.params.kioskId) return path.slice(0, path.lastIndexOf('/')).trim();
  return path;
};

const DashboardRoutes: React.FC<RouteComponentProps> = ({ location }: RouteComponentProps) => {
  const { logout } = useActions({ logout: authActions.logout });
  const { resetVenueAction } = useActions({ resetVenueAction: venueActions.resetAction });
  const path = React.useMemo(() => pathInit(location.pathname), [location.pathname]);
  const [to, setTo] = React.useState<string>(path);
  const classes = useStylesDashboard();
  const admin = useSelector(authSelectors.selectAdmin);
  const [readOnlyAdmin, setReadOnlyAdmin] = useState(false);
  const [globalAdmin, setGlobalAdmin] = useState(false);

  React.useEffect(() => {
    if (!path.includes(to)) setTo(path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  // check for user is Read-Only Admin or not, if yes hide Media center tab
  React.useEffect(() => {
    if (admin && admin.role === 'GlobalAdmin') {
      setGlobalAdmin(true);
    } else if (admin && admin.role === 'ReadOnlyAdmin') {
      setReadOnlyAdmin(true);
    }
  }, [admin]);

  const onRouteChange = (e: React.MouseEvent<HTMLButtonElement>, v: string | number) => setTo(v.toString());

  const clearStoreAndLogout = () => {
    resetVenueAction();
    logout();
  };

  const openSupportLink = () => {
    window.open(`https://kistechnologies.atlassian.net/servicedesk/customer/portal/4`);
  };

  return (
    <Flex bg="white" overflow="hidden" raised flexOverride="1" noWrap style={{ borderRadius: '32px 32px 0 0' }}>
      <div className={classes.dashboardSideTabs}>
        <Flex
          width={236}
          direction="column"
          justify="between"
          fullHeight
          border={{ right: `1px solid ${color.BORDER}` }}
        >
          <Tabs value={to} onChange={onRouteChange}>
            <Tab iconName="kiosks" label="Kiosks" to="/kiosks" />
            <Tab iconName="credential" label="Users" to="/users" />
            <Tab iconName="activity_log" iconViewBox="0 0 59 36" label="Activity Log" to="/activityLog" />
            <Tab iconName="ticket" iconViewBox="-5 0 59 36" label="Delivery Log" to="/deliveryLog" />
            {!readOnlyAdmin && <Tab iconName="media_center" label="Media Center" to="/mediaCenter" />}
            {!readOnlyAdmin && <Tab iconViewBox="0 0 24 24" iconName="settings" label="Settings" to="/settings" />}
            {globalAdmin && (
              <Tab iconViewBox="0 0 24 24" iconName="settings" label="Configuration" to="/configuration" />
            )}
          </Tabs>
          <Flex direction="column" align="center" margin="0 0 5">
            <Box margin="4 0 6">
              <Button size="small" width={144} onClick={openSupportLink}>
                Support
              </Button>
            </Box>
            <Button size="small" width={144} onClick={clearStoreAndLogout}>
              Log Out
            </Button>
            <Box margin="4 0 0">
              <Typography size={12} align="center">
                Version: {packageJSON.version}
              </Typography>
              <Typography size={12} align="center">
                ©KIS Technologies
              </Typography>
            </Box>
          </Flex>
        </Flex>
      </div>
      <Grid className={classes.dashboardInnerContainer} direction="column">
        <Switch>
          <Route exact path="/users" render={() => <UsersView />} />
          <Route exact path="/activityLog" render={() => <ActivityLogView />} />
          <Route exact path="/mediaCenter" render={() => <MediaCenterView />} />
          <Route exact path="/deliveryLog" render={() => <DeliveryLogView />} />
          <Route exact path="/kiosks" render={(props: RouteComponentProps) => <KiosksView {...props} />} />
          {!readOnlyAdmin && (
            <Route exact path="/settings" render={(props: RouteComponentProps) => <SettingsView {...props} />} />
          )}
          {globalAdmin && <Route exact path="/configuration" render={() => <ConfigurationView />} />}
        </Switch>
      </Grid>
    </Flex>
  );
};

export default withRouter(DashboardRoutes);
